<template>
  <div class="searchable-select" :class="{ 'searchable-select-sm': small }" ref="rootRef">
    <button type="button" class="form-select searchable-select-trigger" :disabled="disabled || loading"
      @click="toggleOpen">
      <span class="searchable-select-value" :class="{ 'is-placeholder': !selectedOption }">
        {{ loading ? loadingText : (selectedOption ? selectedOption.name : placeholder) }}
      </span>
    </button>

    <input class="searchable-select-required-proxy" type="text" tabindex="-1" :value="modelValue" :required="required"
      @focus="open" />

    <div v-if="isOpen" class="searchable-select-dropdown">
      <input ref="searchInputRef" type="text" class="form-control searchable-select-search"
        v-model="searchTerm" placeholder="Type to search..." @click.stop @keydown.escape="close" />
      <ul class="searchable-select-options">
        <li v-if="filteredOptions.length === 0" class="searchable-select-empty">No matches found</li>
        <li v-for="option in filteredOptions" :key="option.id" class="searchable-select-option"
          :class="{ 'is-selected': isSelected(option), 'searchable-select-option-all': clearable && option.id === '' }"
          @mousedown.prevent="selectOption(option)">
          {{ option.name }}
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, onBeforeUnmount, nextTick } from 'vue'

// eslint-disable-next-line no-undef
const props = defineProps({
  modelValue: { type: [String, Number], default: '' },
  options: { type: Array, default: () => [] },
  placeholder: { type: String, default: '- Select -' },
  loadingText: { type: String, default: 'Loading...' },
  loading: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
  required: { type: Boolean, default: false },
  small: { type: Boolean, default: false },
  clearable: { type: Boolean, default: false },
})

// eslint-disable-next-line no-undef
const emit = defineEmits(['update:modelValue'])

const isOpen = ref(false)
const searchTerm = ref('')
const rootRef = ref(null)
const searchInputRef = ref(null)

const selectedOption = computed(() =>
  props.options.find(opt => String(opt.id) === String(props.modelValue))
)

const allOption = computed(() => ({ id: '', name: props.placeholder }))

const filteredOptions = computed(() => {
  const term = searchTerm.value.trim().toLowerCase()
  const matches = term
    ? props.options.filter(opt =>
      (opt.name || '').toLowerCase().includes(term) ||
      (opt.orgCode || '').toLowerCase().includes(term)
    )
    : props.options

  return props.clearable ? [allOption.value, ...matches] : matches
})

const isSelected = (option) => String(option.id) === String(props.modelValue)

const open = () => {
  if (props.disabled || props.loading || isOpen.value) return
  isOpen.value = true
  searchTerm.value = ''
  nextTick(() => searchInputRef.value?.focus())
}

const close = () => {
  isOpen.value = false
  searchTerm.value = ''
}

const toggleOpen = () => {
  if (props.disabled || props.loading) return
  isOpen.value ? close() : open()
}

const selectOption = (option) => {
  emit('update:modelValue', option.id)
  // Brief delay before closing so the selection is visibly highlighted first,
  // rather than the dropdown vanishing the instant it's clicked.
  setTimeout(close, 250)
}

const onClickOutside = (e) => {
  if (rootRef.value && !rootRef.value.contains(e.target)) close()
}

onMounted(() => document.addEventListener('mousedown', onClickOutside))
onBeforeUnmount(() => document.removeEventListener('mousedown', onClickOutside))
</script>

<style scoped>
.searchable-select {
  position: relative;
}

.searchable-select-trigger {
  width: 100% !important;
  text-align: left;
  cursor: pointer;
  background-color: var(--input-bg);
  -webkit-appearance: none;
  -moz-appearance: none;
  appearance: none;
  outline: none;
  box-shadow: none;
  color: var(--color);
  border-radius: 0.25rem !important;
  padding: 9px 10px !important;
  border: 1px solid #d8d8d8 !important;
}

.searchable-select-trigger:focus,
.searchable-select-trigger:focus-visible {
  outline: none;
  border-color: #86b7fe;
  box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
}

.searchable-select-trigger:disabled {
  cursor: not-allowed; 
}

.searchable-select-value.is-placeholder {
  color: var(--color);
}

.searchable-select-required-proxy {
  position: absolute;
  top: 0;
  left: 0;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
  opacity: 0;
}

.searchable-select-dropdown {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  z-index: 30;
  background: var(--input-bg);
  border: 1px solid #d1d5db;
  border-radius: 4px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.12);
  overflow: hidden;
}

.searchable-select-search {
  width: 100% !important;
  color: var(--color);
  border-radius: 0.25rem !important;
  padding: 9px 10px !important;
  border: 1px solid #d8d8d8 !important;
  margin: 0 !important;
}

.searchable-select-search:focus {
  box-shadow: none !important;
}

.searchable-select-options {
  list-style: none;
  margin: 0;
  padding: 0 0 0.25rem;
  max-height: 220px;
  overflow-y: auto;
}

.searchable-select-option {
  padding: 0.5rem 0.85rem;
  cursor: pointer;
  font-size: 0.9rem;
}

.searchable-select-option:hover {
  background-color: var(--hover);
}

.searchable-select-option.is-selected {
  background-color: #eff6ff;
  color: #1d4ed8;
  font-weight: 600;
}

.searchable-select-option-all {
  border-bottom: 1px solid #e5e7eb;
}

.searchable-select-empty {
  padding: 0.5rem 0.85rem;
  color: #6b7280;
  font-size: 0.85rem;
}

/* Compact variant to match Bootstrap's .form-select-sm in filter bars */
.searchable-select-sm .searchable-select-trigger,
.searchable-select-sm .searchable-select-search {
  padding: 0.25rem 0.5rem !important;
  font-size: 0.875rem !important;
}

.searchable-select-sm .searchable-select-option {
  padding: 0.4rem 0.65rem;
  font-size: 0.85rem;
}
</style>
